import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as whatsappApi from '../api/whatsappApi';
import { Card, EmptyState, ErrorBanner, Pagination, Skeleton, Spinner, TableSkeleton } from '../components/ui';
import { IconCopy, IconRefresh } from '../components/icons';
import {
  WhatsappMessage,
  WhatsappMessageFilters,
  WhatsappMessageStatus,
  WhatsappMessageSummary,
  WhatsappTemplateCheck,
} from '../types/whatsappMessage';
import { formatDateTime, shortId, timeAgo } from '../utils/format';

const PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 30_000;
const SUMMARY_WINDOW_DAYS = 30;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

// The page opens on the last 30 days so the summary cards describe a meaningful
// window rather than the whole history. Every card and the template breakdown
// are computed over exactly the filters below, so the numbers always match the
// table underneath them.
const defaultFilters = (): WhatsappMessageFilters => ({
  template_name: '',
  status: '',
  phone: '',
  from: isoDaysAgo(SUMMARY_WINDOW_DAYS),
  to: '',
});

const EMPTY_SUMMARY: WhatsappMessageSummary = {
  total_sent: 0,
  total_delivered: 0,
  total_read: 0,
  total_failed: 0,
  delivery_rate: 0,
  templates: [],
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed_transient', label: 'Failed Transient' },
  { value: 'failed_permanent', label: 'Failed Permanent' },
  { value: 'queued', label: 'Queued' },
];

// Delivery lifecycle, not severity: read is the best outcome (they opened it),
// failed_transient is amber because the retry job will try again, and
// failed_permanent is red because nothing more will happen on its own.
const STATUS_BADGE: Record<WhatsappMessageStatus, string> = {
  queued: 'border-edge bg-edge/40 text-sub',
  sent: 'border-edge bg-edge/40 text-sub',
  delivered:
    'border-green-500/30 bg-green-500/10 text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300',
  read: 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-300',
  failed_transient:
    'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300',
  failed_permanent:
    'border-red-500/30 bg-red-500/10 text-red-600 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-300',
};

const STATUS_LABEL: Record<WhatsappMessageStatus, string> = {
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed_transient: 'Failed — retrying',
  failed_permanent: 'Failed',
};

function StatusBadge({ status }: { status: WhatsappMessageStatus }) {
  const cls = STATUS_BADGE[status] ?? STATUS_BADGE.queued;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

const isFailed = (s: WhatsappMessageStatus) => s === 'failed_transient' || s === 'failed_permanent';

function StatCard({ label, value, valueClass = '' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="card p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-sub">{label}</p>
      <p className={`mt-2 text-2xl font-semibold ${valueClass}`}>{value}</p>
    </div>
  );
}

/* ---------- Copy-to-clipboard button ---------- */

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard is unavailable over plain http on a non-localhost host — the id
      // is selectable text either way, so fail quietly rather than alarm the admin.
    }
  };

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <button className="btn-ghost !px-2 !py-1 !text-xs" onClick={copy}>
      <IconCopy className="h-3.5 w-3.5" />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/* ---------- Template health ---------- */

// Compares what the code sends against what each approved template expects. A
// mismatch means every send of that template dies at Meta with 132000 and the
// customer simply never hears from us — the failure that took a day to spot.
function TemplateHealth() {
  const [rows, setRows] = useState<WhatsappTemplateCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await whatsappApi.getTemplateCheck());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const problems = rows.filter((r) => r.status !== 'ok');

  return (
    <Card
      title="Template health"
      action={
        <button className="btn-outline !px-3 !py-1.5 !text-xs" onClick={load} disabled={loading}>
          {loading ? <Spinner className="h-3 w-3" /> : <IconRefresh className="h-3.5 w-3.5" />}
          Re-check
        </button>
      }
    >
      {error ? (
        <ErrorBanner message={error} onRetry={load} />
      ) : loading && rows.length === 0 ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <p className="mb-3 text-xs text-sub">
            {problems.length === 0
              ? 'Every notification template matches the approved version on your WhatsApp Business Account.'
              : `${problems.length} template${problems.length === 1 ? '' : 's'} will fail to send — the code and the approved template disagree.`}
          </p>
          <div className="overflow-x-auto rounded-xl border border-edge">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Template</th>
                  <th className="th">We send</th>
                  <th className="th">Meta expects</th>
                  <th className="th">Language</th>
                  <th className="th">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {rows.map((r) => (
                  <tr key={r.key} className={r.status !== 'ok' ? 'bg-bad/5' : undefined}>
                    <td className="td font-mono text-xs">{r.template_name}</td>
                    <td className="td text-sub">{r.sends}</td>
                    <td className="td text-sub">{r.expects ?? '—'}</td>
                    <td className="td text-sub">{r.languages.join(', ') || '—'}</td>
                    <td className="td">
                      {r.status === 'ok' ? (
                        <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
                          OK
                        </span>
                      ) : r.status === 'missing' ? (
                        <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-0.5 text-xs font-medium text-red-600 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-300">
                          Not on WABA
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-0.5 text-xs font-medium text-red-600 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-300">
                          Mismatch → 132000
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

/* ---------- Expandable message row ---------- */

function MessageRow({
  message,
  expanded,
  onToggle,
}: {
  message: WhatsappMessage;
  expanded: boolean;
  onToggle: () => void;
}) {
  const failed = isFailed(message.status);

  return (
    <>
      <tr className="cursor-pointer transition-colors hover:bg-edge/20" onClick={onToggle}>
        <td className="td">
          <span className="font-medium">{message.recipient_phone}</span>
          {message.customer_name && (
            <span className="mt-0.5 block text-xs text-sub">{message.customer_name}</span>
          )}
        </td>
        <td className="td">
          <span className="inline-flex items-center rounded-lg border border-edge bg-edge/40 px-2 py-0.5 font-mono text-xs text-sub">
            {message.template_name}
          </span>
        </td>
        <td className="td">
          <StatusBadge status={message.status} />
        </td>
        <td className="td">
          {message.order_id ? (
            <Link
              to={`/orders/${message.order_id}`}
              className="font-medium text-gold underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              {shortId(message.order_id)}
            </Link>
          ) : (
            <span className="text-sub">—</span>
          )}
        </td>
        <td className={`td ${message.attempt_count > 1 ? 'font-medium text-warn' : 'text-sub'}`}>
          {message.attempt_count}
        </td>
        <td className="td text-sub">{formatDateTime(message.created_at)}</td>
        <td className="td text-sub">{timeAgo(message.updated_at ? new Date(message.updated_at) : null)}</td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={7} className="bg-edge/10 px-5 py-4">
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-sub">wa_message_id</span>
                {message.wa_message_id ? (
                  <>
                    <span className="break-all font-mono text-xs">{message.wa_message_id}</span>
                    <CopyButton value={message.wa_message_id} />
                  </>
                ) : (
                  <span className="text-sub">— not assigned (never accepted by Meta)</span>
                )}
              </div>

              {message.last_attempt_at && (
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-sub">Last attempt</span>
                  <span className="text-sub">{formatDateTime(message.last_attempt_at)}</span>
                </div>
              )}

              {failed && (message.error_code || message.error_message) && (
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-sub">Error</span>
                  <span className="text-bad">
                    {message.error_code ? `[${message.error_code}] ` : ''}
                    {message.error_message || 'Failed'}
                  </span>
                </div>
              )}

              {failed && (
                <p className="pt-1 text-xs text-sub">
                  {message.status === 'failed_transient'
                    ? 'The retry job re-sends this automatically — it runs every 5 minutes, up to 5 attempts.'
                    : 'The retry job has given up on this one and will not try again.'}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ---------- Page ---------- */

export default function WhatsappMessagesPage() {
  // `draft` is what the filter bar shows; `applied` is what the API is called
  // with. They diverge until the admin hits Apply, so typing a phone number
  // doesn't fire a request per keystroke.
  const [draft, setDraft] = useState<WhatsappMessageFilters>(defaultFilters);
  const [applied, setApplied] = useState<WhatsappMessageFilters>(defaultFilters);
  const [page, setPage] = useState(1);

  const [messages, setMessages] = useState<WhatsappMessage[]>([]);
  const [summary, setSummary] = useState<WhatsappMessageSummary>(EMPTY_SUMMARY);
  const [templateNames, setTemplateNames] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);


  // silent keeps the table on screen during an auto-refresh — swapping in the
  // skeleton every 30s would make the log unreadable while it's being watched.
  const load = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const res = await whatsappApi.getMessages({ ...applied, page, limit: PAGE_SIZE });
        setMessages(res.messages);
        setSummary(res.summary);
        setTemplateNames(res.templateNames);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setLastUpdated(new Date());
      } catch (err) {
        setError(apiErrorMessage(err));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [applied, page]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => load(true), AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const setDraftField = (patch: Partial<WhatsappMessageFilters>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const applyFilters = () => {
    setPage(1);
    setExpandedId(null);
    setApplied(draft);
  };

  const clearFilters = () => {
    const next = defaultFilters();
    setPage(1);
    setExpandedId(null);
    setDraft(next);
    setApplied(next);
  };

  // The cards always describe the applied range — say which one, so "Total sent"
  // is never mistaken for an all-time number.
  const rangeLabel =
    applied.from && applied.to
      ? `${applied.from} → ${applied.to}`
      : applied.from
      ? `since ${applied.from}`
      : applied.to
      ? `up to ${applied.to}`
      : 'all time';

  const showSkeleton = loading && messages.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">WhatsApp Messages</h1>
        <p className="text-sm text-sub">
          Every outbound template send and where it got to
          {total > 0 ? ` · ${total} message${total === 1 ? '' : 's'} in this view` : ''}
        </p>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => load()} />}

      {/* Summary cards */}
      {showSkeleton ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard label="Total sent" value={String(summary.total_sent)} />
            <StatCard label="Delivered" value={String(summary.total_delivered)} valueClass="text-ok" />
            <StatCard label="Read" value={String(summary.total_read)} valueClass="text-blue-500 dark:text-blue-300" />
            <StatCard label="Failed" value={String(summary.total_failed)} valueClass="text-bad" />
            <StatCard label="Delivery rate" value={`${summary.delivery_rate}%`} valueClass="text-gold" />
          </div>
          <p className="mt-2 text-xs text-sub">
            Counted over the applied filters ({rangeLabel}). Delivery rate = delivered + read, as a share of
            messages Meta accepted.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="card flex flex-wrap items-end gap-3 p-4">
        <div className="w-full sm:w-56">
          <label className="label">Template</label>
          <select
            className="input"
            value={draft.template_name ?? ''}
            onChange={(e) => setDraftField({ template_name: e.target.value })}
          >
            <option value="">All templates</option>
            {templateNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full sm:w-44">
          <label className="label">Status</label>
          <select
            className="input"
            value={draft.status ?? ''}
            onChange={(e) => setDraftField({ status: e.target.value })}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <form
          className="w-full min-w-[160px] flex-1 sm:w-auto"
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters();
          }}
        >
          <label className="label">Phone</label>
          <input
            className="input"
            placeholder="partial number…"
            value={draft.phone ?? ''}
            onChange={(e) => setDraftField({ phone: e.target.value })}
          />
        </form>

        <div className="w-full sm:w-40">
          <label className="label">From</label>
          <input
            type="date"
            className="input"
            value={draft.from ?? ''}
            onChange={(e) => setDraftField({ from: e.target.value })}
          />
        </div>
        <div className="w-full sm:w-40">
          <label className="label">To</label>
          <input
            type="date"
            className="input"
            value={draft.to ?? ''}
            onChange={(e) => setDraftField({ to: e.target.value })}
          />
        </div>

        <button className="btn-gold" onClick={applyFilters}>
          Apply filters
        </button>
        <button className="btn-ghost" onClick={clearFilters}>
          Clear
        </button>
      </div>

      {/* Messages table */}
      <Card
        bodyClassName="!p-0"
        title="Delivery log"
        action={
          <div className="flex flex-wrap items-center gap-3">
            {lastUpdated && (
              <span className="hidden text-xs text-sub sm:inline">
                Updated {timeAgo(lastUpdated)}
              </span>
            )}
            <label className="flex cursor-pointer items-center gap-2 text-xs text-sub">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-edge accent-gold"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (30s)
            </label>
            <button
              className="btn-outline !px-3 !py-1.5 !text-xs"
              onClick={() => load(true)}
              disabled={loading || refreshing}
            >
              {refreshing ? <Spinner className="h-3 w-3" /> : <IconRefresh className="h-3.5 w-3.5" />}
              Refresh
            </button>
          </div>
        }
      >
        {showSkeleton ? (
          <TableSkeleton rows={8} />
        ) : messages.length === 0 ? (
          <EmptyState message="No messages found" hint="Try widening the date range or clearing the filters" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Recipient</th>
                    <th className="th">Template</th>
                    <th className="th">Status</th>
                    <th className="th">Order</th>
                    <th className="th">Attempts</th>
                    <th className="th">Sent At</th>
                    <th className="th">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {messages.map((m) => (
                    <MessageRow
                      key={m.id}
                      message={m}
                      expanded={expandedId === m.id}
                      onToggle={() => setExpandedId((prev) => (prev === m.id ? null : m.id))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Card>

      {/* Template health — code vs what's approved on the WABA */}
      <TemplateHealth />

      {/* Template breakdown — which templates this view is actually made of */}
      {!showSkeleton && summary.templates.length > 0 && (
        <Card title="By template">
          <div className="flex flex-wrap gap-2">
            {summary.templates.map((t) => (
              <span
                key={t.name}
                className="inline-flex items-center gap-2 rounded-lg border border-edge bg-edge/30 px-3 py-1.5 text-xs"
              >
                <span className="font-mono text-sub">{t.name}</span>
                <span className="font-semibold">{t.count}</span>
              </span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
